import { FilterBar } from 'core/components/ui/FilterBar'
import { PageHeader } from 'core/components/ui/PageHeader'
import React, { useState } from 'react'
import { OrderListItem, OrderStatus } from '../../domain/entities/Order'
import { OrderDetailModal } from '../components/OrderDetailModal'
import { OrdersTable } from '../components/OrdersTable'
import { useOrders } from '../hooks/useOrders'

const orderStatuses: { value: OrderStatus; label: string }[] = [
  { value: 'indent_raised', label: 'Indent Placed' },
  { value: 'order_placed', label: 'Order Placed' },
  { value: 'payment_received', label: 'Payment Received' },
  { value: 'order_confirmed', label: 'Order Confirmed' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'dispatched', label: 'Dispatched' },
  { value: 'delivered', label: 'Delivered' },
]

export const OrdersPage: React.FC = () => {
  const {
    orders,
    loading,
    error,
    search,
    setSearch,
    status,
    setStatus,
    page,
    setPage,
    totalPages,
    totalItems,
    updateStatus,
    resetFilters,
  } = useOrders(10)

  const [selectedOrder, setSelectedOrder] = useState<OrderListItem | null>(null)
  const [detailOpen, setDetailOpen] = useState(false)

  const handleReviewClick = (order: OrderListItem) => {
    setSelectedOrder(order)
    setDetailOpen(true)
  }

  const handleUpdateStatus = async (nextStatus: OrderStatus) => {
    if (!selectedOrder) return
    // Let concurrency conflicts / fetch errors propagate to the modal so it can display them.
    await updateStatus(selectedOrder.id, nextStatus, selectedOrder.version)
    // Close detail modal on status change to prevent stale display
    setDetailOpen(false)
    setSelectedOrder(null)
  }

  const isFilterActive = !!(search || status !== undefined)

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <PageHeader
        title="Order Registry"
        description="Monitor, approve, and track B2B cattle feed indents throughout the order status lifecycle."
        breadcrumbs={[{ label: 'Portal', path: '/' }, { label: 'Orders' }]}
      />

      {/* Fetch error banner */}
      {error && (
        <div className="p-3 text-sm bg-danger/10 border border-danger/20 text-danger rounded-btn font-medium">
          {error}
        </div>
      )}

      {/* Filter Bar */}
      <FilterBar
        searchPlaceholder="Search order ID, buyer, address or contact..."
        searchValue={search}
        onSearchChange={setSearch}
        showReset={isFilterActive}
        onReset={resetFilters}
      >
        {/* Status Filter */}
        <select
          value={status === undefined ? '' : String(status)}
          onChange={(e) => {
            const val = e.target.value
            setStatus(val === '' ? undefined : (val as OrderStatus))
            setPage(1)
          }}
          className="px-3 py-2 border border-border rounded-btn bg-surface text-text-primary text-xs font-semibold focus:outline-none"
        >
          <option value="">All Statuses</option>
          {orderStatuses.map((st) => (
            <option key={st.value} value={st.value}>
              {st.label}
            </option>
          ))}
        </select>
      </FilterBar>

      {/* Table Data */}
      <OrdersTable
        orders={orders}
        loading={loading}
        onReview={handleReviewClick}
        currentPage={page}
        totalPages={totalPages}
        onPageChange={setPage}
        totalItems={totalItems}
        itemsPerPage={10}
      />

      {/* Details Side-drawer modal */}
      <OrderDetailModal
        isOpen={detailOpen}
        onClose={() => setDetailOpen(false)}
        order={selectedOrder}
        onUpdateStatus={handleUpdateStatus}
      />
    </div>
  )
}
